import Route from 'cio/routes/base';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { DEFAULT_UTM_PARAM_VALUES } from 'cio/utils/analytics/utm';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import MasqService from 'cio/services/masq';
import SignupService from 'cio/services/signup';
import ReleaseNotificationService from 'cio/services/release-notification';
import BillingService from 'cio/services/billing';
import Transition from '@ember/routing/-private/transition';
import { redirectAwayAndAlert } from 'cio/utils/route-access';
import DS from 'ember-data';
import { announceConfirmTransition, subscribeToTransitionAttempt } from '@customerio/hydra-nav-state';
import { subscribeToRefreshWorkspaces } from '@customerio/hydra-nav-state';
import AppController from './controller';
import { CONFIRM_TRANSITION } from 'cio/services/event-bus';
import { schedule } from '@ember/runloop';
import HydraService from 'cio/services/hydra';
import FeaturesService from 'ember-feature-flags/services/features';

export default class AppRoute extends Route {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service account!: AccountService;
  @service masq!: MasqService;
  @service router!: RouterService;
  @service signup!: SignupService;
  @service releaseNotification!: ReleaseNotificationService;
  @service billing!: BillingService;
  @service store!: DS.Store;
  @service hydra!: HydraService;
  @service features!: FeaturesService;

  async beforeModel(transition: Transition) {
    // When the account is canceled or the user has no workspaces, we don't
    // need to load release notifications. We should just redirect.

    let restrictAccessNoEnvironment = this.account.isJourneysActivated && !this.auth.user?.defaultEnvironment?.id;
    if ((this.account.current.canceled || restrictAccessNoEnvironment) && !this.masq.isOn) {
      await super.beforeModel(transition);
      return this.checkRouteAccess(transition);
    }

    try {
      this.billing.initialize({ refresh: true, paramPlanSlug: '', code: '', ignoreErrors: true });
    } catch {
      // if request fail, user would not be able to see accurate message
      // if account is overdue but can use the app anyway.
    }

    this.releaseNotification.fetch.perform();
    await super.beforeModel(transition);
    return this.checkRouteAccess(transition);
  }

  async checkRouteAccess(transition: Transition) {
    let account = this.account.current;
    let restrictAccessNoEnvironment = this.account.isJourneysActivated && !this.auth.user?.defaultEnvironment?.id;
    let accessible = this.masq.isOn || (!account.suspended && !account.canceled && !restrictAccessNoEnvironment);
    if (accessible) {
      return;
    }
    let redirectTo: string | [string, { queryParams: object }] = ''; // overwritten later
    if (this.account.current.canceled && !this.masq.isOn) {
      redirectTo = 'authenticated.account_canceled';
    } else if (this.account.current.suspended) {
      // Account is suspended but we should not redirect away from checkout
      if (this.auth.hasPermission('account/billing/view') && transition.to.name === 'settings.billing.checkout') {
        return;
      }

      let redirect = '';
      // if the user is not an admin, they should be redirected to the team page
      if (!this.auth.hasPermission('account/billing/view')) {
        redirect = 'settings.team';
        // if the user is an admin and they are on a premium trial
      } else if (this.account.current.premiumTrial) {
        // check to see if they have a pinned plan -- if yes, redirect to checkout
        if (this.billing.pinnedPlan) {
          redirect = 'settings.billing.checkout';
          // if they do not have a pinned plan, take them to available plans
        } else {
          redirect = 'settings.billing.available-plans.journeys';
        }
        // if the account is not a premium trial, send them to checkout
      } else if (!this.account.current.premiumTrial) {
        await this.billing.initialize({ refresh: true, paramPlanSlug: '', code: '', ignoreErrors: true });
        if (this.billing.billingInfo?.activeSubscription === true) {
          redirect = 'settings.billing.payment-methods';
        } else {
          redirect = 'settings.billing.available-plans.journeys';
        }
      }
      redirectTo = [redirect, { queryParams: { suspended: true } }];
    } else if (restrictAccessNoEnvironment) {
      redirectTo = 'no_accessible_workspaces';
    }

    // Don't display an alert if account is suspended or journeys trial is expired
    // Suspended users are sent to the subscription page and shown a modal
    let alertMessage = this.account.current.suspended || this.account.planFeatures.messaging.expired ? '' : undefined;

    return redirectAwayAndAlert(this, transition, { redirectTo, alertMessage });
  }

  async model() {
    return this.store.peekAll('environment');
  }

  @action
  didTransition() {
    // Resetting query params on the controller will fire another transition.
    // That doesn't really interfere with anything, but it will cause another
    // analytics tracking event, which is annoying.
    //
    // There's now an extra check in utils/analytics/generate-transition-event
    // to prevent this additional tracking event from happening.
    this.controller.setProperties(DEFAULT_UTM_PARAM_VALUES as any);
    return true;
  }

  unsubscribeListeners: (() => void)[] = [];

  activate() {
    super.activate();

    if (!this.hydra.isHydraEnabled) {
      return;
    }

    this.unsubscribeListeners.push(subscribeToTransitionAttempt(this.onAttemptTransition));
    this.unsubscribeListeners.push(subscribeToRefreshWorkspaces(this.onRefreshWorkspaces));
  }

  deactivate() {
    super.deactivate();

    if (!this.hydra.isHydraEnabled) {
      return;
    }

    this.unsubscribeListeners.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeListeners = [];
  }

  @action
  onAttemptTransition() {
    if (!this.hydra.isHydraEnabled) {
      return;
    }

    let controller = this.controllerFor('app') as AppController;
    let confirmChangesManager = controller.confirmChanges.getManagerInstance();

    if (confirmChangesManager != null) {
      if (confirmChangesManager.isDirty) {
        controller.eventBus.publish(CONFIRM_TRANSITION, {
          retry: () => {
            // Discarding changes may sometimes trigger an internal transition,
            // which would interfere with the Hydra workspace transition.
            // Confirming the Hydra transition after the next render loop avoids
            // that problem.
            schedule('afterRender', () => {
              announceConfirmTransition();
            });
            return;
          },
        });
        return;
      }
    }

    announceConfirmTransition();
  }

  @action
  async onRefreshWorkspaces() {
    if (this.hydra.isHydraEnabled) {
      // Refresh the current account and user records, to pull in the updated
      // environments properly
      await this.store.queryRecord('account', {});
    }
  }
}
